const surveyTranslations = {
  en: {
    // Authentication
    "auth.companyEmployeeSurvey": ({ companyName }) =>
      `${companyName} Employee Survey`,
    "auth.enterPasswordPin": "Enter your password pin",
    "auth.login": "Login",

    // Welcome
    "welcome.title": ({ firstName }) => `Welcome ${firstName}`,
    "welcome.description": ({ companyName }) =>
      `${companyName} is using Aclymate to automatically calculate and offset their monthly carbon emissions. As an employee of ${companyName}, your commuting and home office emissions make up part of your company's footprint. In this short survey, we ask you to provide us with the information we need to automatically calculate your commuting and home office emissions every month and help ${companyName} become carbon neutral.`,
    "welcome.getStarted": "Let's get started",

    // Thank you
    "thankYou.label": "Thank you",
    "thankYou.title": "Thank you",
    "thankYou.subtitle":
      "Thank you for completing the survey! You may now close this window.",

    // Errors
    "error.occurred": "An error has occurred.",
    "error.reloadPage":
      "Please reload this page directly from the email you received.",
    "error.upgradeSubscription": ({ companyName }) =>
      `${companyName} needs to upgrade their subscription`,
    "error.upgradeSubtitle":
      "Your company's current subscription needs to be upgraded to access this survey.",
    "error.pageExpired": "This page is expired",
    "error.pageExpiredSubtitle":
      "If you believe you are seeing this message in error, please contact the Aclymate administrator for your company.",

    // Common
    "common.pleaseWait": "Please wait...",
    "common.yes": "Yes",
    "common.no": "No",
    "common.submit": "Submit",
    "common.nextStep": "Next Step",

    // Input type titles
    "inputType.employees.title":
      "Invite your team to get a better picture of how they contribute to your company's overall emissions",
    "inputType.employeeSurvey.title": "Employee Survey",

    // Personal Information
    "personalInfo.label": "Personal Information",
    "personalInfo.title": "Let's start with your basic personal information",
    "personalInfo.firstName": "What is your first name?",
    "personalInfo.lastName": "What is your last name?",
    "personalInfo.email": "What is your email address?",
    "personalInfo.emailError": "This must be a valid email address.",
    "personalInfo.homeAddress":
      "What is your home address, zip code, or city that you live in?",
    "personalInfo.startedBefore": ({ date }) =>
      `Did you start working here before ${date}?`,
    "personalInfo.startDate": "When did you start your job?",

    // Vehicles
    "vehicles.label": "Commute Vehicles",
    "vehicles.anyVehiclesQuestion":
      "Have you driven any personal vehicles to work since you started here?",
    "vehicles.noVehicles": "You don't have any personal commute vehicles",
    "vehicles.firstVehicleTitle":
      "Great! Let's start by inputting some car info below",
    "vehicles.nextVehicleTitle": "Now tell us about this vehicle",
    "vehicles.anotherVehicle": "Have you used another vehicle to commute?",
    "vehicles.vehicleMake": "Vehicle Make",
    "vehicles.vehicleModel": "Vehicle Model",
    "vehicles.vehicleYear": "Vehicle Year",
    "vehicles.vehicleName": "Vehicle Name",
    "vehicles.fuelType": "Fuel Type",
    "vehicles.fuelTypes": "Fuel Types",
    "vehicles.vehicleType": "Vehicle Type",
    "vehicles.backToRegular": "Back to regular vehicles",
    "vehicles.nameError":
      "You need to have at least one non-numeric character in the name and the name cannot have more than 75 characters",
    "vehicles.dontSeeYour": "don't see your vehicle?",
    "vehicles.altWalkBike": "Walking/Biking",
    "vehicles.altLightRail": "Light Rail/Subway",
    "vehicles.altRegionalRail": "Regional Rail",
    "vehicles.altIntercityRail": "Intercity Rail",
    "vehicles.altCarpool": "Carpooling",
    "vehicles.altBus": "Bus",

    // Commute Schedule Graphic
    "commute.day": ({ num }) => `Day ${num}`,
    "commute.daysPerWeekShort": ({ days }) => `${days} days/wk.`,
    "commute.daysPerWeekLong": ({ days }) => `${days} days/week`,
    "commute.dragVehicles": "Drag commute vehicles into this block",
    "commute.addAnotherVehicle": "Add another vehicle to this commute day",
    "commute.officeClosed":
      "This office is now closed and needs to be changed",
    "commute.onLeave": "On leave",
    "commute.removeVehicleQuestion": "Would you like to remove this vehicle?",
    "commute.removeWorkspaceQuestion":
      "Would you like to remove this workspace?",
    "commute.currentWeeklyCommute": ({ firstName }) =>
      `${firstName}'s current weekly commute schedule`,
    "commute.weeklyCommuteFromTo": ({ firstName, startDate, endDate }) =>
      `${firstName}'s weekly commute schedule from ${startDate} to ${endDate}`,
    "commute.weeklyCommuteBefore": ({ firstName, endDate }) =>
      `${firstName}'s weekly commute schedule before ${endDate}`,

    // Commute edit popper (shown when clicking a workspace/vehicle block)
    "commutePopper.selectWorkspace": "Select a workspace",
    "commutePopper.selectCompanyOffice": "Select a company office",
    "commutePopper.homeArea": "Home square footage",
    "commutePopper.homeOfficeArea": "Home office square footage",
    "commutePopper.homeRenewablesPercent":
      "Home office electric renewables percentage",
    "commutePopper.homeAddress": "What is your home address?",
    "commutePopper.officeAddress": ({ label }) => `${label} address`,
    "commutePopper.workspaceCompanyOffice": "Company Office",
    "commutePopper.workspaceHomeOffice": "Home Office",
    "commutePopper.workspaceCoffeeShop": "Coffee Shop",
    "commutePopper.workspaceCoworking": "Coworking Space",
    "commutePopper.changeOfficeTooltip":
      "Edit just this office without adding a new one",
    "commutePopper.changeOffice": "Change this office",
    "commutePopper.addOfficeTooltip":
      "Add this office to this time period if you're commuting to this place in addition to the one that's previously shown",
    "commutePopper.addOffice": "Make this a new office",
    "commutePopper.editOrAddVehicle": "Edit or add a vehicle",
    "commutePopper.selectCommuteVehicle": "Select a commute vehicle",
    "commutePopper.saveChanges": "Save Changes",
    "commutePopper.addVehicle": "Add a vehicle",

    // Workspaces
    "workspaces.label": "Workspaces",
    "workspaces.title":
      "Now we need to know where you work and some information about each of those workspaces",
    "workspaces.titlePriorTo": ({ date }) =>
      `Now we need to know where you worked prior to ${date}`,
    "workspaces.validationError":
      "You need to have at least one workplace selected before moving on.",
    "workspaces.submitError":
      "We couldn't save your answers. Please check your connection and try again.",
    "workspaces.selectOffices": "Select company offices",
    "workspaces.selectOfficesHelper":
      "Please select all of the offices you work in during an average week",
    "workspaces.companyOffice": ({ count }) =>
      count === 1 ? "the company office" : "any company offices",
    "workspaces.homeOffice": "your home office",
    "workspaces.coffeeShops": "any coffee shops",
    "workspaces.coworkingSpaces": "any coworking spaces",
    "workspaces.duringAvgWeek": ({ endDate, question }) =>
      `During an average week, ${endDate ? "did" : "do"} you work from ${question}?`,
    "workspaces.previousHomeQuestion": ({ address }) =>
      `During this time, did you still live at ${address}?`,
    "workspaces.homeSize": "How big is your home?",
    "workspaces.sqFootageHelper": "Please enter a square footage",
    "workspaces.homeOfficeSize": "How big is your home office?",
    "workspaces.renewableEnergy":
      "Is your home powered by renewable energy sources?",
    "workspaces.renewablePercentage":
      "What % of electricity is from renewables?",
    "workspaces.coffeeShopLabel": "Coffee Shop",
    "workspaces.coworkingLabel": "Coworking Space",
    "workspaces.coffeeShopPlaceholder":
      "What is the Coffee Shop Name or Address",
    "workspaces.coworkingPlaceholder":
      "What is the Coworking Space Name or Address",
    "workspaces.addAnother": ({ label }) =>
      `Add another ${label} where you work`,
    "workspaces.otherHomeAddress": "What is the other home's address?",
    "workspaces.officeFarAway": ({ miles }) =>
      `An office you selected is ${miles} miles away from your home.`,
    "workspaces.homeOfficeLabel": "Home Office",

    // Work Schedule
    "schedule.label": "Work Schedule",
    "schedule.title":
      "We're going to use the graphic to the right to build your commute schedule. A commute schedule includes the workspaces you're commuting to, the vehicles you're using to get there, and how often you travel to and work from each workspace.",
    "schedule.instructions":
      "Let's start with how many days per week you work. Drag the slider on the chart to the right to adjust your work schedule then move onto the next step.",
    "schedule.weekend": "Weekend",
    "schedule.workWeek": "Work week",

    // Commute Schedule
    "commuteSchedule.label": "Commute Schedule",
    "commuteSchedule.title":
      "Great, now we need to know how often you work in each of these workspaces and how you get there.",
    "commuteSchedule.instructions":
      "We've placed all of your workspaces on this graphic to represent how many days per week you work in each one. The actual day of the week doesn't matter (e.g. Wednesday vs. Thursday), only how many days per week. Drag the arrows to adjust how often you work in each workspace and then move on to the next step.",

    // Commute Vehicles (drag step)
    "commuteVehicles.label": "Commute Vehicles",
    "commuteVehicles.title":
      "Now we need to know which vehicles you use to commute to each location.",
    "commuteVehicles.instructions":
      "Drag a vehicle from the options below into each office and then drag it like you dragged the offices to adjust how many days per week you commute to that office with that vehicle.",
    "commuteVehicles.yourVehicles": "Your Vehicles",
    "commuteVehicles.yourVehiclesAnnotation":
      "These are vehicles owned by you that you defined earlier in this form. You can go back to that step if you need to edit these vehicles.",
    "commuteVehicles.companyVehicles": "Company Vehicles",
    "commuteVehicles.companyVehiclesAnnotation":
      "These are vehicles that are owned and managed by your company that can be used by employees like you.",
    "commuteVehicles.alternateTransportation": "Alternate Transportation",
    "commuteVehicles.alternateAnnotation":
      "These are alternate forms of transportation that don't involve driving a vehicle to work.",
    "commuteVehicles.noVehiclesAvailable":
      "There are no personal vehicles available",

    // Schedule Start Date
    "startDate.label": "Schedule Start Date",
    "startDate.workedSince": ({ date }) =>
      `Have you worked this schedule since at least ${date}.`,
    "startDate.whenDidYouStart": "When did you start this schedule?",
    "startDate.whenDidYouStartWorking":
      "When did you start working with this schedule?",

    // Time Period titles
    "timePeriod.fromTo": ({ startDate, endDate }) =>
      `Commute Schedule from ${startDate} to ${endDate}`,
    "timePeriod.fromToNow": ({ startDate }) =>
      `Commute Schedule from ${startDate} to now`,
    "timePeriod.priorTo": ({ endDate }) =>
      `Commute Schedule prior to ${endDate}`,
    "timePeriod.default": "Commute Schedule",
  },
  es: {
    // Authentication
    "auth.companyEmployeeSurvey": ({ companyName }) =>
      `Encuesta de Empleados de ${companyName}`,
    "auth.enterPasswordPin": "Ingrese su PIN de contraseña",
    "auth.login": "Iniciar sesión",

    // Welcome
    "welcome.title": ({ firstName }) =>
      `Te damos la bienvenida, ${firstName}`,
    "welcome.description": ({ companyName }) =>
      `${companyName} está utilizando Aclymate para calcular y compensar automáticamente sus emisiones mensuales de carbono. Como miembro del equipo de ${companyName}, tus emisiones por desplazamiento y oficina en casa forman parte de la huella de carbono de tu empresa. En esta breve encuesta, te pedimos que nos proporciones la información necesaria para calcular automáticamente tus emisiones por desplazamiento y oficina en casa cada mes y ayudar a ${companyName} a ser carbono neutral.`,
    "welcome.getStarted": "Comencemos",

    // Thank you
    "thankYou.label": "Gracias",
    "thankYou.title": "Gracias",
    "thankYou.subtitle":
      "¡Gracias por completar la encuesta! Ahora puede cerrar esta ventana.",

    // Errors
    "error.occurred": "Ha ocurrido un error.",
    "error.reloadPage":
      "Por favor, recargue esta página directamente desde el correo electrónico que recibió.",
    "error.upgradeSubscription": ({ companyName }) =>
      `${companyName} necesita actualizar su suscripción`,
    "error.upgradeSubtitle":
      "La suscripción actual de su empresa necesita ser actualizada para acceder a esta encuesta.",
    "error.pageExpired": "Esta página ha expirado",
    "error.pageExpiredSubtitle":
      "Si cree que está viendo este mensaje por error, por favor contacte al administrador de Aclymate de su empresa.",

    // Common
    "common.pleaseWait": "Por favor espere...",
    "common.yes": "Sí",
    "common.no": "No",
    "common.submit": "Enviar",
    "common.nextStep": "Siguiente",

    // Input type titles
    "inputType.employees.title":
      "Invita a tu equipo para obtener una mejor idea de cómo contribuyen a las emisiones generales de tu empresa",
    "inputType.employeeSurvey.title": "Encuesta para Empleados",

    // Personal Information
    "personalInfo.label": "Información Personal",
    "personalInfo.title": "Comencemos con tu información personal básica",
    "personalInfo.firstName": "¿Cuál es tu nombre?",
    "personalInfo.lastName": "¿Cuál es tu apellido?",
    "personalInfo.email": "¿Cuál es tu dirección de correo electrónico?",
    "personalInfo.emailError":
      "Esta debe ser una dirección de correo electrónico válida.",
    "personalInfo.homeAddress":
      "¿Cuál es tu dirección, código postal o ciudad donde vives?",
    "personalInfo.startedBefore": ({ date }) =>
      `¿Comenzaste a trabajar aquí antes del ${date}?`,
    "personalInfo.startDate": "¿Cuándo comenzaste tu trabajo?",

    // Vehicles
    "vehicles.label": "Vehículos de Transporte",
    "vehicles.anyVehiclesQuestion":
      "¿Has conducido algún vehículo personal al trabajo desde que comenzaste aquí?",
    "vehicles.noVehicles":
      "No tienes ningún vehículo personal de transporte",
    "vehicles.firstVehicleTitle":
      "¡Genial! Comencemos ingresando la información del vehículo a continuación",
    "vehicles.nextVehicleTitle": "Ahora cuéntanos sobre este vehículo",
    "vehicles.anotherVehicle":
      "¿Has usado otro vehículo para ir al trabajo?",
    "vehicles.vehicleMake": "Marca del Vehículo",
    "vehicles.vehicleModel": "Modelo del Vehículo",
    "vehicles.vehicleYear": "Año del Vehículo",
    "vehicles.vehicleName": "Nombre del Vehículo",
    "vehicles.fuelType": "Tipo de Combustible",
    "vehicles.fuelTypes": "Tipos de Combustible",
    "vehicles.vehicleType": "Tipo de Vehículo",
    "vehicles.backToRegular": "Volver a vehículos regulares",
    "vehicles.nameError":
      "Necesitas tener al menos un carácter no numérico en el nombre y el nombre no puede tener más de 75 caracteres",
    "vehicles.dontSeeYour": "¿No ves tu vehículo?",
    "vehicles.altWalkBike": "Caminata/Bicicleta",
    "vehicles.altLightRail": "Tren Ligero/Metro",
    "vehicles.altRegionalRail": "Tren Regional",
    "vehicles.altIntercityRail": "Tren Interurbano",
    "vehicles.altCarpool": "Compartir Vehículo",
    "vehicles.altBus": "Autobús",

    // Commute Schedule Graphic
    "commute.day": ({ num }) => `Día ${num}`,
    "commute.daysPerWeekShort": ({ days }) => `${days} días/sem.`,
    "commute.daysPerWeekLong": ({ days }) => `${days} días/semana`,
    "commute.dragVehicles":
      "Arrastra vehículos de transporte a este bloque",
    "commute.addAnotherVehicle":
      "Agrega otro vehículo a este día de transporte",
    "commute.officeClosed":
      "Esta oficina ahora está cerrada y necesita ser cambiada",
    "commute.onLeave": "De licencia",
    "commute.removeVehicleQuestion":
      "¿Te gustaría eliminar este vehículo?",
    "commute.removeWorkspaceQuestion":
      "¿Te gustaría eliminar este espacio de trabajo?",
    "commute.currentWeeklyCommute": ({ firstName }) =>
      `Horario de transporte semanal actual de ${firstName}`,
    "commute.weeklyCommuteFromTo": ({ firstName, startDate, endDate }) =>
      `Horario de transporte semanal de ${firstName} del ${startDate} al ${endDate}`,
    "commute.weeklyCommuteBefore": ({ firstName, endDate }) =>
      `Horario de transporte semanal de ${firstName} antes del ${endDate}`,

    // Commute edit popper (shown when clicking a workspace/vehicle block)
    "commutePopper.selectWorkspace": "Selecciona un espacio de trabajo",
    "commutePopper.selectCompanyOffice": "Selecciona una oficina de la empresa",
    "commutePopper.homeArea": "Pies cuadrados de la casa",
    "commutePopper.homeOfficeArea": "Pies cuadrados de la oficina en casa",
    "commutePopper.homeRenewablesPercent":
      "Porcentaje de energía renovable de la oficina en casa",
    "commutePopper.homeAddress": "¿Cuál es tu dirección de casa?",
    "commutePopper.officeAddress": ({ label }) => `Dirección del ${label}`,
    "commutePopper.workspaceCompanyOffice": "Oficina de la Empresa",
    "commutePopper.workspaceHomeOffice": "Oficina en Casa",
    "commutePopper.workspaceCoffeeShop": "Cafetería",
    "commutePopper.workspaceCoworking": "Espacio de Coworking",
    "commutePopper.changeOfficeTooltip":
      "Editar solo esta oficina sin agregar una nueva",
    "commutePopper.changeOffice": "Cambiar esta oficina",
    "commutePopper.addOfficeTooltip":
      "Agrega esta oficina a este período de tiempo si también te desplazas a este lugar además del que se muestra anteriormente",
    "commutePopper.addOffice": "Hacer de esta una nueva oficina",
    "commutePopper.editOrAddVehicle": "Editar o agregar un vehículo",
    "commutePopper.selectCommuteVehicle": "Selecciona un vehículo de transporte",
    "commutePopper.saveChanges": "Guardar Cambios",
    "commutePopper.addVehicle": "Agregar un vehículo",

    // Workspaces
    "workspaces.label": "Espacios de Trabajo",
    "workspaces.title":
      "Ahora necesitamos saber dónde trabajas y alguna información sobre cada uno de esos espacios de trabajo",
    "workspaces.titlePriorTo": ({ date }) =>
      `Ahora necesitamos saber dónde trabajaste antes del ${date}`,
    "workspaces.validationError":
      "Necesitas tener al menos un lugar de trabajo seleccionado antes de continuar.",
    "workspaces.submitError":
      "No pudimos guardar tus respuestas. Verifica tu conexión e inténtalo de nuevo.",
    "workspaces.selectOffices": "Seleccionar oficinas de la empresa",
    "workspaces.selectOfficesHelper":
      "Por favor selecciona todas las oficinas en las que trabajas durante una semana promedio",
    "workspaces.companyOffice": ({ count }) =>
      count === 1
        ? "la oficina de la empresa"
        : "alguna oficina de la empresa",
    "workspaces.homeOffice": "tu oficina en casa",
    "workspaces.coffeeShops": "alguna cafetería",
    "workspaces.coworkingSpaces": "algún espacio de coworking",
    "workspaces.duringAvgWeek": ({ endDate, question }) =>
      `Durante una semana promedio, ¿${endDate ? "trabajabas" : "trabajas"} desde ${question}?`,
    "workspaces.previousHomeQuestion": ({ address }) =>
      `Durante este tiempo, ¿seguías viviendo en ${address}?`,
    "workspaces.homeSize": "¿Qué tan grande es tu casa?",
    "workspaces.sqFootageHelper": "Por favor ingresa los pies cuadrados",
    "workspaces.homeOfficeSize": "¿Qué tan grande es tu oficina en casa?",
    "workspaces.renewableEnergy":
      "¿Tu casa se alimenta de fuentes de energía renovable?",
    "workspaces.renewablePercentage":
      "¿Qué porcentaje de electricidad proviene de fuentes renovables?",
    "workspaces.coffeeShopLabel": "Cafetería",
    "workspaces.coworkingLabel": "Espacio de Coworking",
    "workspaces.coffeeShopPlaceholder":
      "¿Cuál es el nombre o dirección de la cafetería?",
    "workspaces.coworkingPlaceholder":
      "¿Cuál es el nombre o dirección del espacio de coworking?",
    "workspaces.addAnother": ({ label }) =>
      `Agregar otra ${label} donde trabajes`,
    "workspaces.otherHomeAddress":
      "¿Cuál es la dirección de la otra casa?",
    "workspaces.officeFarAway": ({ miles }) =>
      `Una oficina que seleccionaste está a ${miles} millas de tu casa.`,
    "workspaces.homeOfficeLabel": "Oficina en Casa",

    // Work Schedule
    "schedule.label": "Horario de Trabajo",
    "schedule.title":
      "Vamos a usar el gráfico a la derecha para construir tu horario de transporte. Un horario de transporte incluye los espacios de trabajo a los que te desplazas, los vehículos que usas para llegar y con qué frecuencia viajas y trabajas desde cada espacio.",
    "schedule.instructions":
      "Comencemos con cuántos días por semana trabajas. Arrastra el control deslizante en el gráfico a la derecha para ajustar tu horario de trabajo y luego pasa al siguiente paso.",
    "schedule.weekend": "Fin de semana",
    "schedule.workWeek": "Semana laboral",

    // Commute Schedule
    "commuteSchedule.label": "Horario de Transporte",
    "commuteSchedule.title":
      "Ahora necesitamos saber con qué frecuencia trabajas en cada uno de estos espacios de trabajo y cómo llegas allí.",
    "commuteSchedule.instructions":
      "Hemos colocado todos tus espacios de trabajo en este gráfico para representar cuántos días por semana trabajas en cada uno. El día real de la semana no importa (por ejemplo, miércoles vs. jueves), solo cuántos días por semana. Arrastra las flechas para ajustar con qué frecuencia trabajas en cada espacio de trabajo y luego pasa al siguiente paso.",

    // Commute Vehicles (drag step)
    "commuteVehicles.label": "Vehículos de Transporte",
    "commuteVehicles.title":
      "Ahora necesitamos saber qué vehículos usas para desplazarte a cada ubicación.",
    "commuteVehicles.instructions":
      "Arrastra un vehículo de las opciones a continuación a cada oficina y luego arrástralo como arrastraste las oficinas para ajustar cuántos días por semana te desplazas a esa oficina con ese vehículo.",
    "commuteVehicles.yourVehicles": "Tus Vehículos",
    "commuteVehicles.yourVehiclesAnnotation":
      "Estos son vehículos de tu propiedad que definiste anteriormente en este formulario. Puedes volver a ese paso si necesitas editar estos vehículos.",
    "commuteVehicles.companyVehicles": "Vehículos de la Empresa",
    "commuteVehicles.companyVehiclesAnnotation":
      "Estos son vehículos que son propiedad y están administrados por tu empresa y que pueden ser utilizados por empleados como tú.",
    "commuteVehicles.alternateTransportation": "Transporte Alternativo",
    "commuteVehicles.alternateAnnotation":
      "Estas son formas alternativas de transporte que no implican conducir un vehículo al trabajo.",
    "commuteVehicles.noVehiclesAvailable":
      "No hay vehículos personales disponibles",

    // Schedule Start Date
    "startDate.label": "Fecha de Inicio del Horario",
    "startDate.workedSince": ({ date }) =>
      `¿Has trabajado con este horario desde al menos el ${date}?`,
    "startDate.whenDidYouStart": "¿Cuándo comenzaste este horario?",
    "startDate.whenDidYouStartWorking":
      "¿Cuándo comenzaste a trabajar con este horario?",

    // Time Period titles
    "timePeriod.fromTo": ({ startDate, endDate }) =>
      `Horario de Transporte del ${startDate} al ${endDate}`,
    "timePeriod.fromToNow": ({ startDate }) =>
      `Horario de Transporte del ${startDate} hasta ahora`,
    "timePeriod.priorTo": ({ endDate }) =>
      `Horario de Transporte antes del ${endDate}`,
    "timePeriod.default": "Horario de Transporte",
  },
  fr: {
    // Authentication
    "auth.companyEmployeeSurvey": ({ companyName }) =>
      `Sondage des Employés de ${companyName}`,
    "auth.enterPasswordPin": "Entrez votre code PIN",
    "auth.login": "Connexion",

    // Welcome
    "welcome.title": ({ firstName }) => `Bienvenue ${firstName}`,
    "welcome.description": ({ companyName }) =>
      `${companyName} utilise Aclymate pour calculer et compenser automatiquement ses émissions mensuelles de carbone. En tant qu'employé(e) de ${companyName}, vos émissions liées au transport et au bureau à domicile font partie de l'empreinte carbone de votre entreprise. Dans ce court sondage, nous vous demandons de nous fournir les informations nécessaires pour calculer automatiquement vos émissions chaque mois et aider ${companyName} à devenir carboneutre.`,
    "welcome.getStarted": "Commençons",

    // Thank you
    "thankYou.label": "Merci",
    "thankYou.title": "Merci",
    "thankYou.subtitle":
      "Merci d'avoir complété le sondage! Vous pouvez maintenant fermer cette fenêtre.",

    // Errors
    "error.occurred": "Une erreur s'est produite.",
    "error.reloadPage":
      "Veuillez recharger cette page directement depuis le courriel que vous avez reçu.",
    "error.upgradeSubscription": ({ companyName }) =>
      `${companyName} doit mettre à niveau son abonnement`,
    "error.upgradeSubtitle":
      "L'abonnement actuel de votre entreprise doit être mis à niveau pour accéder à ce sondage.",
    "error.pageExpired": "Cette page a expiré",
    "error.pageExpiredSubtitle":
      "Si vous pensez voir ce message par erreur, veuillez contacter l'administrateur Aclymate de votre entreprise.",

    // Common
    "common.pleaseWait": "Veuillez patienter...",
    "common.yes": "Oui",
    "common.no": "Non",
    "common.submit": "Soumettre",
    "common.nextStep": "Suivant",

    // Input type titles
    "inputType.employees.title":
      "Invitez votre équipe pour avoir une meilleure idée de leur contribution aux émissions globales de votre entreprise",
    "inputType.employeeSurvey.title": "Sondage des Employés",

    // Personal Information
    "personalInfo.label": "Informations Personnelles",
    "personalInfo.title":
      "Commençons par vos informations personnelles de base",
    "personalInfo.firstName": "Quel est votre prénom?",
    "personalInfo.lastName": "Quel est votre nom de famille?",
    "personalInfo.email": "Quelle est votre adresse courriel?",
    "personalInfo.emailError":
      "Ceci doit être une adresse courriel valide.",
    "personalInfo.homeAddress":
      "Quelle est votre adresse, code postal ou ville où vous habitez?",
    "personalInfo.startedBefore": ({ date }) =>
      `Avez-vous commencé à travailler ici avant le ${date}?`,
    "personalInfo.startDate": "Quand avez-vous commencé votre emploi?",

    // Vehicles
    "vehicles.label": "Véhicules de Transport",
    "vehicles.anyVehiclesQuestion":
      "Avez-vous conduit des véhicules personnels pour vous rendre au travail depuis que vous avez commencé ici?",
    "vehicles.noVehicles":
      "Vous n'avez aucun véhicule personnel de transport",
    "vehicles.firstVehicleTitle":
      "Super! Commençons par entrer les informations du véhicule ci-dessous",
    "vehicles.nextVehicleTitle":
      "Maintenant, parlez-nous de ce véhicule",
    "vehicles.anotherVehicle":
      "Avez-vous utilisé un autre véhicule pour vous rendre au travail?",
    "vehicles.vehicleMake": "Marque du Véhicule",
    "vehicles.vehicleModel": "Modèle du Véhicule",
    "vehicles.vehicleYear": "Année du Véhicule",
    "vehicles.vehicleName": "Nom du Véhicule",
    "vehicles.fuelType": "Type de Carburant",
    "vehicles.fuelTypes": "Types de Carburant",
    "vehicles.vehicleType": "Type de Véhicule",
    "vehicles.backToRegular": "Retour aux véhicules réguliers",
    "vehicles.nameError":
      "Le nom doit contenir au moins un caractère non numérique et ne peut pas dépasser 75 caractères",
    "vehicles.dontSeeYour": "Vous ne voyez pas votre véhicule?",
    "vehicles.altWalkBike": "Marche/Vélo",
    "vehicles.altLightRail": "Train Léger/Métro",
    "vehicles.altRegionalRail": "Train Régional",
    "vehicles.altIntercityRail": "Train Interurbain",
    "vehicles.altCarpool": "Covoiturage",
    "vehicles.altBus": "Autobus",

    // Commute Schedule Graphic
    "commute.day": ({ num }) => `Jour ${num}`,
    "commute.daysPerWeekShort": ({ days }) => `${days} j/sem.`,
    "commute.daysPerWeekLong": ({ days }) => `${days} jours/semaine`,
    "commute.dragVehicles":
      "Glissez les véhicules de transport dans ce bloc",
    "commute.addAnotherVehicle":
      "Ajouter un autre véhicule à ce jour de transport",
    "commute.officeClosed":
      "Ce bureau est maintenant fermé et doit être modifié",
    "commute.onLeave": "En congé",
    "commute.removeVehicleQuestion":
      "Voulez-vous retirer ce véhicule?",
    "commute.removeWorkspaceQuestion":
      "Voulez-vous retirer cet espace de travail?",
    "commute.currentWeeklyCommute": ({ firstName }) =>
      `Horaire de transport hebdomadaire actuel de ${firstName}`,
    "commute.weeklyCommuteFromTo": ({ firstName, startDate, endDate }) =>
      `Horaire de transport hebdomadaire de ${firstName} du ${startDate} au ${endDate}`,
    "commute.weeklyCommuteBefore": ({ firstName, endDate }) =>
      `Horaire de transport hebdomadaire de ${firstName} avant le ${endDate}`,

    // Commute edit popper (shown when clicking a workspace/vehicle block)
    "commutePopper.selectWorkspace": "Sélectionnez un espace de travail",
    "commutePopper.selectCompanyOffice":
      "Sélectionnez un bureau de l'entreprise",
    "commutePopper.homeArea": "Superficie de la maison",
    "commutePopper.homeOfficeArea": "Superficie du bureau à domicile",
    "commutePopper.homeRenewablesPercent":
      "Pourcentage d'énergie renouvelable du bureau à domicile",
    "commutePopper.homeAddress": "Quelle est votre adresse?",
    "commutePopper.officeAddress": ({ label }) => `Adresse du ${label}`,
    "commutePopper.workspaceCompanyOffice": "Bureau de l'Entreprise",
    "commutePopper.workspaceHomeOffice": "Bureau à Domicile",
    "commutePopper.workspaceCoffeeShop": "Café",
    "commutePopper.workspaceCoworking": "Espace de Coworking",
    "commutePopper.changeOfficeTooltip":
      "Modifier seulement ce bureau sans en ajouter un nouveau",
    "commutePopper.changeOffice": "Modifier ce bureau",
    "commutePopper.addOfficeTooltip":
      "Ajoutez ce bureau à cette période si vous vous déplacez à cet endroit en plus de celui affiché précédemment",
    "commutePopper.addOffice": "En faire un nouveau bureau",
    "commutePopper.editOrAddVehicle": "Modifier ou ajouter un véhicule",
    "commutePopper.selectCommuteVehicle":
      "Sélectionnez un véhicule de transport",
    "commutePopper.saveChanges": "Enregistrer les Modifications",
    "commutePopper.addVehicle": "Ajouter un véhicule",

    // Workspaces
    "workspaces.label": "Espaces de Travail",
    "workspaces.title":
      "Maintenant, nous devons savoir où vous travaillez et quelques informations sur chacun de ces espaces de travail",
    "workspaces.titlePriorTo": ({ date }) =>
      `Maintenant, nous devons savoir où vous travailliez avant le ${date}`,
    "workspaces.validationError":
      "Vous devez sélectionner au moins un lieu de travail avant de continuer.",
    "workspaces.submitError":
      "Nous n'avons pas pu enregistrer vos réponses. Vérifiez votre connexion et réessayez.",
    "workspaces.selectOffices": "Sélectionnez les bureaux de l'entreprise",
    "workspaces.selectOfficesHelper":
      "Veuillez sélectionner tous les bureaux où vous travaillez pendant une semaine moyenne",
    "workspaces.companyOffice": ({ count }) =>
      count === 1
        ? "le bureau de l'entreprise"
        : "un des bureaux de l'entreprise",
    "workspaces.homeOffice": "votre bureau à domicile",
    "workspaces.coffeeShops": "des cafés",
    "workspaces.coworkingSpaces": "des espaces de coworking",
    "workspaces.duringAvgWeek": ({ endDate, question }) =>
      `Pendant une semaine moyenne, ${endDate ? "travailliez" : "travaillez"}-vous depuis ${question}?`,
    "workspaces.previousHomeQuestion": ({ address }) =>
      `Pendant cette période, habitiez-vous toujours à ${address}?`,
    "workspaces.homeSize": "Quelle est la taille de votre maison?",
    "workspaces.sqFootageHelper":
      "Veuillez entrer une superficie en pieds carrés",
    "workspaces.homeOfficeSize":
      "Quelle est la taille de votre bureau à domicile?",
    "workspaces.renewableEnergy":
      "Votre maison est-elle alimentée par des sources d'énergie renouvelable?",
    "workspaces.renewablePercentage":
      "Quel pourcentage d'électricité provient de sources renouvelables?",
    "workspaces.coffeeShopLabel": "Café",
    "workspaces.coworkingLabel": "Espace de Coworking",
    "workspaces.coffeeShopPlaceholder":
      "Quel est le nom ou l'adresse du café?",
    "workspaces.coworkingPlaceholder":
      "Quel est le nom ou l'adresse de l'espace de coworking?",
    "workspaces.addAnother": ({ label }) =>
      `Ajouter un autre ${label} où vous travaillez`,
    "workspaces.otherHomeAddress": "Quelle est l'adresse de l'autre maison?",
    "workspaces.officeFarAway": ({ miles }) =>
      `Un bureau que vous avez sélectionné est à ${miles} milles de votre domicile.`,
    "workspaces.homeOfficeLabel": "Bureau à Domicile",

    // Work Schedule
    "schedule.label": "Horaire de Travail",
    "schedule.title":
      "Nous allons utiliser le graphique à droite pour construire votre horaire de transport. Un horaire de transport inclut les espaces de travail où vous vous déplacez, les véhicules que vous utilisez pour y arriver et à quelle fréquence vous voyagez et travaillez depuis chaque espace.",
    "schedule.instructions":
      "Commençons par le nombre de jours par semaine où vous travaillez. Faites glisser le curseur sur le graphique à droite pour ajuster votre horaire de travail puis passez à l'étape suivante.",
    "schedule.weekend": "Fin de semaine",
    "schedule.workWeek": "Semaine de travail",

    // Commute Schedule
    "commuteSchedule.label": "Horaire de Transport",
    "commuteSchedule.title":
      "Super, maintenant nous devons savoir à quelle fréquence vous travaillez dans chacun de ces espaces de travail et comment vous y arrivez.",
    "commuteSchedule.instructions":
      "Nous avons placé tous vos espaces de travail sur ce graphique pour représenter combien de jours par semaine vous travaillez dans chacun. Le jour réel de la semaine n'a pas d'importance (par exemple, mercredi vs jeudi), seulement le nombre de jours par semaine. Faites glisser les flèches pour ajuster à quelle fréquence vous travaillez dans chaque espace de travail puis passez à l'étape suivante.",

    // Commute Vehicles (drag step)
    "commuteVehicles.label": "Véhicules de Transport",
    "commuteVehicles.title":
      "Maintenant, nous devons savoir quels véhicules vous utilisez pour vous déplacer vers chaque emplacement.",
    "commuteVehicles.instructions":
      "Glissez un véhicule parmi les options ci-dessous vers chaque bureau puis faites-le glisser comme vous avez glissé les bureaux pour ajuster le nombre de jours par semaine où vous vous déplacez à ce bureau avec ce véhicule.",
    "commuteVehicles.yourVehicles": "Vos Véhicules",
    "commuteVehicles.yourVehiclesAnnotation":
      "Ce sont des véhicules qui vous appartiennent et que vous avez définis plus tôt dans ce formulaire. Vous pouvez revenir à cette étape si vous devez modifier ces véhicules.",
    "commuteVehicles.companyVehicles": "Véhicules de l'Entreprise",
    "commuteVehicles.companyVehiclesAnnotation":
      "Ce sont des véhicules appartenant à votre entreprise et gérés par elle, qui peuvent être utilisés par des employés comme vous.",
    "commuteVehicles.alternateTransportation": "Transport Alternatif",
    "commuteVehicles.alternateAnnotation":
      "Ce sont des formes alternatives de transport qui n'impliquent pas la conduite d'un véhicule au travail.",
    "commuteVehicles.noVehiclesAvailable":
      "Aucun véhicule personnel disponible",

    // Schedule Start Date
    "startDate.label": "Date de Début de l'Horaire",
    "startDate.workedSince": ({ date }) =>
      `Avez-vous travaillé avec cet horaire depuis au moins le ${date}?`,
    "startDate.whenDidYouStart": "Quand avez-vous commencé cet horaire?",
    "startDate.whenDidYouStartWorking":
      "Quand avez-vous commencé à travailler avec cet horaire?",

    // Time Period titles
    "timePeriod.fromTo": ({ startDate, endDate }) =>
      `Horaire de Transport du ${startDate} au ${endDate}`,
    "timePeriod.fromToNow": ({ startDate }) =>
      `Horaire de Transport du ${startDate} à maintenant`,
    "timePeriod.priorTo": ({ endDate }) =>
      `Horaire de Transport avant le ${endDate}`,
    "timePeriod.default": "Horaire de Transport",
  },
};
export default surveyTranslations;
